import type {
  DevelopmentRole,
  ProjectNode,
  VisibilityTier,
  Workstream,
} from '@/shared/types/projectData';
import { findProjectNodeById, scopedNodeId } from './nodeTypeConversionPolicy';

const WORKSTREAM_VALUES = new Set<Workstream>([
  'development',
  'meeting',
  'sales',
  'admin',
  'general',
  'planning',
  'research',
  'strategy',
  'build',
  'review',
  'delivery',
  'ops',
]);

const VISIBILITY_TIER_VALUES = new Set<VisibilityTier>([
  'internal',
  'partner',
  'client',
]);

const DEVELOPMENT_ROLE_VALUES = new Set<DevelopmentRole>([
  'architecture',
  'product',
  'planning',
  'ux',
  'ui',
  'frontend',
  'backend',
  'mcp',
  'fullstack',
  'mobile',
  'qa',
  'devops',
  'data',
  'ai',
  'ai_ml',
  'visual',
  'writing',
  'security',
  'integration',
]);

export interface ParentCreateDefaults {
  visibilityTier?: VisibilityTier;
  visibleToAccountIds?: string[];
  workstream?: Workstream;
  developmentRoles?: DevelopmentRole[];
}

const normalizeWorkstream = (value: unknown): Workstream | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase() as Workstream;
  return WORKSTREAM_VALUES.has(normalized) ? normalized : undefined;
};

const normalizeVisibilityTier = (value: unknown): VisibilityTier | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase() as VisibilityTier;
  return VISIBILITY_TIER_VALUES.has(normalized) ? normalized : undefined;
};

const normalizeVisibleToAccountIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const normalized: string[] = [];
  value.forEach((rawValue) => {
    const accountId = String(rawValue ?? '').trim();
    if (!accountId || seen.has(accountId)) return;
    seen.add(accountId);
    normalized.push(accountId);
  });
  return normalized;
};

const normalizeDevelopmentRoles = (value: unknown): DevelopmentRole[] => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<DevelopmentRole>();
  const normalized: DevelopmentRole[] = [];
  value.forEach((rawValue) => {
    const role = String(rawValue ?? '').trim().toLowerCase() as DevelopmentRole;
    if (!DEVELOPMENT_ROLE_VALUES.has(role) || seen.has(role)) return;
    seen.add(role);
    normalized.push(role);
  });
  return normalized;
};

const hasOwn = (value: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

export const resolveParentCreateDefaults = (
  nodes: ProjectNode[],
  parentId?: string | null,
): ParentCreateDefaults => {
  if (!parentId) return {};

  const parent = findProjectNodeById(nodes, parentId);
  if (!parent) return {};

  const inherited = resolveParentCreateDefaults(nodes, scopedNodeId(parent, parent.parentId));
  const parentWorkstream = normalizeWorkstream(parent.workstream) ?? inherited.workstream;
  const parentVisibilityTier = normalizeVisibilityTier(parent.visibilityTier) ?? inherited.visibilityTier;
  const parentVisibleToAccountIds = normalizeVisibleToAccountIds(parent.visibleToAccountIds);
  const inheritedVisibleToAccountIds = inherited.visibleToAccountIds ?? [];
  const effectiveVisibleToAccountIds = parentVisibleToAccountIds.length > 0
    ? parentVisibleToAccountIds
    : inheritedVisibleToAccountIds;
  const parentDevelopmentRoles = normalizeDevelopmentRoles(parent.developmentRoles);
  const inheritedDevelopmentRoles = inherited.developmentRoles ?? [];
  const parentExplicitlySetsDevelopmentRoles = hasOwn(parent, 'developmentRoles');

  return {
    ...(parentVisibilityTier ? { visibilityTier: parentVisibilityTier } : {}),
    ...(effectiveVisibleToAccountIds.length > 0
      ? { visibleToAccountIds: effectiveVisibleToAccountIds }
      : {}),
    ...(parentWorkstream ? { workstream: parentWorkstream } : {}),
    ...(parentWorkstream === 'development'
      ? {
          developmentRoles: parentDevelopmentRoles.length > 0
            ? parentDevelopmentRoles
            : parentExplicitlySetsDevelopmentRoles
              ? []
              : inheritedDevelopmentRoles,
        }
      : { developmentRoles: [] }),
  };
};

export const applyParentCreateDefaults = <T extends Partial<ProjectNode>>(
  node: T,
  defaults: ParentCreateDefaults,
): T => {
  const next: Partial<ProjectNode> = { ...node };

  if (defaults.visibilityTier && !hasOwn(next, 'visibilityTier')) {
    next.visibilityTier = defaults.visibilityTier;
  }

  if (defaults.visibleToAccountIds && !hasOwn(next, 'visibleToAccountIds')) {
    next.visibleToAccountIds = [...defaults.visibleToAccountIds];
  }

  if (defaults.workstream && !hasOwn(next, 'workstream')) {
    next.workstream = defaults.workstream;
  }

  const finalWorkstream = normalizeWorkstream(next.workstream) ?? defaults.workstream;
  if (finalWorkstream === 'development') {
    if (!hasOwn(next, 'developmentRoles') && defaults.developmentRoles) {
      next.developmentRoles = [...defaults.developmentRoles];
    }
  } else {
    next.developmentRoles = [];
  }

  return next as T;
};
